import { v4 as uuidv4 } from 'uuid';
import type { DashboardDraft } from '../types';

/**
 * The draft's page algebra: the three transformations the tab bar performs
 * (PRD Feature 2).
 *
 * Pure functions over the draft, outside React, for the same reason as
 * `groupOperations` — and here it weighs more. Deleting a page touches **three**
 * collections at once (`pages`, `groups`, `widgets`), because the groups of a
 * page and the widgets inside those groups go with it; the prototype learned
 * that one by leaving orphans behind that still counted in its header, still got
 * saved, and still weighed on choosing the dashboard's main table.
 *
 * `DashboardDraftContext` is the only caller. Display text never reaches here:
 * the default name of a new page is resolved with i18n by whoever asks for the
 * page (see `useDashboardPages`) and arrives as an argument.
 */

/**
 * The page a group belongs to, or `undefined` if no page claims it.
 *
 * The inverse of `groupIdsOfPage`, and it exists because **selecting something
 * has to bring its page with it**: a widget is only visible while its page is
 * the one on screen (see `activeWidgetId` in `DashboardDraftContext`), so
 * selecting one on another page would point the panel at something nobody can
 * see — which reads as the selection having failed.
 *
 * A scan and not an index: a dashboard has a handful of pages, and an index
 * would be a second structure to keep in step with `pages[].groupIds`.
 */
export function pageIdOfGroup(draft: DashboardDraft, groupId: string): string | undefined {
  return draft.pages.find((page) => page.groupIds.includes(groupId))?.id;
}

/** Names as they are compared: trimmed and case-insensitive. */
const normalise = (name: string): string => name.trim().toLowerCase();

/**
 * Whether another page already answers to this name.
 *
 * `exceptPageId` is the page being renamed, so a page does not collide with
 * itself. Only real names are compared — a page with no name is not "taken", and
 * the replacement label its tab shows is the tab bar's business, not the draft's
 * (see `useDashboardPages`).
 */
/**
 * The name a new page gets when nobody typed one.
 *
 * Pulled out of `useDashboardPages` so the two ways a page can be born — the
 * `+` in the page bar, and a group or widget created on a dashboard that has
 * none — hand out the same name from the same rule. Two copies would drift, and
 * the drift would only show in the second language.
 *
 * `makeName` receives a 1-based position and returns the localised name, which
 * is what keeps this file free of i18n. The search climbs from past the pages
 * that exist until the name is free, rather than parsing the existing names:
 * the prototype's `^Page (\d+)$` recognised only its own English output, so in
 * Spanish it matched nothing and handed out "Página 1" forever.
 *
 * Pages with no name of their own count as taken too: the bar labels them by
 * position with this same sentence, so "1" is occupied even though the draft
 * holds no name for it.
 */
export function nextPageName(draft: DashboardDraft, makeName: (index: number) => string): string {
  const collides = (name: string) => {
    if (isPageNameTaken(draft, name)) return true;

    const wanted = normalise(name);
    if (!wanted) return false;

    return draft.pages.some(
      (page, index) => !page.name.trim() && normalise(makeName(index + 1)) === wanted,
    );
  };

  let index = draft.pages.length + 1;
  while (collides(makeName(index))) index += 1;
  return makeName(index);
}

export function isPageNameTaken(
  draft: DashboardDraft,
  name: string,
  exceptPageId?: string,
): boolean {
  const wanted = normalise(name);
  if (!wanted) return false;

  return draft.pages.some((page) => page.id !== exceptPageId && normalise(page.name) === wanted);
}

export interface AddPageResult {
  draft: DashboardDraft;
  /** The page just created — the caller makes it the visible one. */
  pageId: string;
  /** The group it was born with, which becomes the destination. */
  groupId: string;
}

/**
 * Appends a page at the end of the bar, born with **one empty group**.
 *
 * The group is not optional: the active group is always a group of the visible
 * page (PRD §7), so a page with none would leave whatever gets added next
 * without a destination. It is the same rule that keeps the last group of a page
 * from being deleted, seen from the other end.
 *
 * The name arrives resolved. Ids are minted here, the way `createEmptyDraft`
 * does, and handed back so the caller can show the page and its group.
 */
export function addPage(draft: DashboardDraft, name: string): AddPageResult {
  const pageId = uuidv4();
  const groupId = uuidv4();

  return {
    pageId,
    groupId,
    draft: {
      ...draft,
      pages: [...draft.pages, { id: pageId, name, groupIds: [groupId] }],
      groups: [...draft.groups, { id: groupId, widgetIds: [] }],
    },
  };
}

/**
 * Renames a page.
 *
 * Stores it trimmed, because the name is what tells two tabs apart and trailing
 * space would make two identical-looking tabs different names.
 *
 * An empty name is a no-op and not an erase: a tab is selected by its name, and
 * clearing it would turn "I changed my mind" into a page nobody can point at.
 * Uniqueness is NOT checked here — the caller has to be able to say why it
 * refused, which is copy (see `useDashboardPages.isNameTaken`).
 */
export function renamePage(draft: DashboardDraft, pageId: string, name: string): DashboardDraft {
  const trimmed = name.trim();
  if (!trimmed) return draft;

  return {
    ...draft,
    pages: draft.pages.map((page) => (page.id === pageId ? { ...page, name: trimmed } : page)),
  };
}

/**
 * Deletes a page, its groups and the widgets those groups held.
 *
 * Everything at once, so nothing is left addressable by nothing: a group whose
 * page is gone cannot be reached, and a widget whose group is gone would still
 * be counted and still be saved (PRD §11: no orphan definitions).
 *
 * Refuses the last page. A dashboard with zero pages has no canvas to draw.
 */
export function deletePage(draft: DashboardDraft, pageId: string): DashboardDraft {
  const victim = draft.pages.find((page) => page.id === pageId);
  if (!victim || draft.pages.length <= 1) return draft;

  const doomedGroups = new Set(victim.groupIds);
  const doomedWidgets = new Set(
    draft.groups.filter((group) => doomedGroups.has(group.id)).flatMap((group) => group.widgetIds),
  );

  return {
    ...draft,
    pages: draft.pages.filter((page) => page.id !== pageId),
    groups: draft.groups.filter((group) => !doomedGroups.has(group.id)),
    widgets: draft.widgets.filter((widget) => !doomedWidgets.has(widget.id)),
  };
}
